import { Button, Col, Grid, Row, Spin, Typography } from "antd";
import { ContactModal } from "../ContactModal/web";
import { FinancingInput } from "../FinancingInput/FinancingInput";
import { ImagenOptimizada } from "../../Image/web";
import { InputWithPercentage, Select } from "../dataInputs";
import { useItauFinancingV2 } from "./hooks/useItauFinancingV2";
import { useTheme } from "../../../Styles/ThemeHook";
import "./styles.less";
import { formatMoney } from "@Utils/Functions";
import { LoadingIcon } from "shared-components/Components/CustomIcons/LoadingIcon/LoadingIcon";

const { useBreakpoint } = Grid;

const DESKTOP_SOURCE = "infocasas_desktop";
const MOBILE_SOURCE = "infocasas_mobile";

export const ItauFinancingV2 = ({
	id,
	isDisabledProperty = false,
	showFinancingCard = false,
	financingRef = null,
}) => {
	const { theme } = useTheme();
	const screen = useBreakpoint();
	const source = screen.lg ? DESKTOP_SOURCE : MOBILE_SOURCE;

	const {
		loan: { amount: loan, change: setLoan },
		property: { amount: propertyPrice },
		years: { value: years, change: setYears, totalYears },
		type: { value: type, change: setType, options: types },
		setIsDefaultSimulation,
		payment,
		propertyCurrency,
		show,
		loading,
		error,
	} = useItauFinancingV2({ id, source, showFinancingCard });

	const handleLoanChange = (value, type) => {
		const num = Number(value);
		if (type === "plus") setLoan(Math.round(num + 100));
		else if (type === "minus") setLoan(Math.round(num - 100));
		else setLoan(Math.round(num));
	};

	const onScrollToFinancing = () => {
		if (financingRef) {
			const componentFinancing = financingRef.current.offsetTop;
			const withAffixHeight = screen.lg ? 30 : 0;
			const scrollingTo = componentFinancing - withAffixHeight;
			window.scrollTo({ top: scrollingTo, behavior: "smooth" });
		}
	};

	if (!show) return null;

	if (showFinancingCard) {
		return (
			<div className="property-financing-itau-card-v2" style={{ width: "100%" }}>
				{payment &&
				!loading &&
				!error &&
				Math.round((loan * 100) / propertyPrice) <= 80 &&
				loan >= 40_000 ? (
					<div className="itau-header">
						Compra esta propiedad pagando <b>U$S {formatMoney(propertyPrice - loan)}</b>{" "}
						y cuotas de <b>$ {formatMoney(payment)}</b>
					</div>
				) : null}
				<div className="itau-footer">
					<div className="itau-logo">
						<ImagenOptimizada
							src="https://cdn4.fincaraiz.com.co/web/69384a631c7b7_logo_itau.svg"
							alt="Itau"
						/>
					</div>
					<Button onClick={onScrollToFinancing} className="itau-btn">
						Consultar por préstamo
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="property-financing-itau-v2">
			<Row gutter={[0, theme.spacing.mdSpacing]}>
				<Col span={24} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
					<Typography.Title style={{ marginBottom: "0" }} level={4}>
						{"Financiación"}
					</Typography.Title>
					<div className="itau-logo">
						<ImagenOptimizada
							src="https://cdn4.fincaraiz.com.co/web/69384a631c7b7_logo_itau.svg"
							alt="ITAÚ"
							width={30}
							height={30}
						/>
					</div>
				</Col>
				<Col xs={24} md={16}>
					<div className="property-financing-box">
						<Row gutter={[theme.spacing.xxlSpacing, theme.spacing.mdSpacing]}>
							<Col xs={24}>
								<FinancingInput
									title="Valor de la propiedad"
									input={
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												backgroundColor: "rgb(255, 255, 255)",
												height: "40px",
												width: "100%",
												borderWidth: "1px",
												borderStyle: "solid",
												borderColor: "rgb(217, 217, 217)",
												borderRadius: "8px",
												padding: "0px 11px",
											}}>
											<Typography.Text
												style={{ marginBottom: "0" }}>{`U$S ${formatMoney(
												propertyPrice
											)}`}</Typography.Text>
										</div>
									}
									fullWidth
								/>
							</Col>
							<Col xs={24}>
								<FinancingInput
									title="Importe a Solicitar"
									input={
										<InputWithPercentage
											isDisabledProperty={isDisabledProperty}
											value={loan}
											max={propertyPrice}
											onChange={(value, type) => {
												handleLoanChange(value, type);
												setIsDefaultSimulation(false);
											}}
											percentage={Math.round((loan * 100) / propertyPrice)}
											currency={propertyCurrency}
										/>
									}
									fullWidth
								/>
							</Col>
							<Col xs={24} md={12}>
								<FinancingInput
									title="Moneda del préstamo"
									input={
										<Select
											isDisabledProperty={isDisabledProperty}
											value={type}
											options={types}
											onChange={value => {
												setType(value);
												setIsDefaultSimulation(false);
											}}
										/>
									}
									fullWidth
								/>
							</Col>
							<Col xs={24} md={12}>
								<FinancingInput
									title="Plazo"
									input={
										<Select
											isDisabledProperty={isDisabledProperty}
											value={years}
											options={totalYears}
											onChange={value => {
												setYears(value);
												setIsDefaultSimulation(false);
											}}
										/>
									}
									fullWidth
								/>
							</Col>
						</Row>
					</div>
				</Col>
				<Col xs={24} md={8} className="property-financing-monthly-itau">
					{payment &&
					Math.round((loan * 100) / propertyPrice) <= 80 &&
					loan >= 40_000 &&
					!loading &&
					!error ? (
						<>
							<Typography.Title>{"Tu cuota mensual:"}</Typography.Title>
							<Typography.Title className="financing-price">
								{type === "UI" ? "$" : "U$S"} {formatMoney(payment)}
							</Typography.Title>
							<Button
								disabled={isDisabledProperty}
								className="itau-financing-button"
								onClick={() => {
									const params = new URLSearchParams();
									params.append("hipotecarios", "true");
									params.append("origen", source);
									params.append("montoPrestamo", Number(loan).toString());
									params.append("moneda", type);
									params.append("plazo", (years * 12).toString());
									params.append(
										"valorPropiedad",
										Number(propertyPrice).toString()
									);
									window.open(
										`https://www.itau.com.uy/inst/solicitudPrestamoHipotecario.html?${params.toString()}`,
										"_blank"
									);
								}}>
								{"Quiero solicitarlo"}
							</Button>
							<Typography.Text className="financing-price-small">
								{
									"*Producto sujeto a aprobación crediticia. El valor de la cuota es de referencia según el valor de la UI al día de la fecha y no contempla gastos administrativos. Las condiciones contractuales a las que estará sujeto el otorgamiento de estos créditos están disponibles en "
								}
								<a
									target="_blank"
									className="financing-price-small-link"
									rel="noopener noreferrer"
									href="https://www.itau.com.uy/">
									itau.com.uy
								</a>
							</Typography.Text>
						</>
					) : loading ? (
						<Typography.Text
							className="financing-price-small"
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: "8px",
								fontSize: "16px",
							}}>
							<LoadingIcon classes="loading-icon" width={16} />
							{"Cargando..."}
						</Typography.Text>
					) : loan < 40_000 ? (
						<Typography.Text
							className="financing-price-small"
							style={{ fontSize: "16px" }}>
							El valor debe ser como mínimo 40000
						</Typography.Text>
					) : Math.round((loan * 100) / propertyPrice) > 80 ? (
						<Typography.Text
							className="financing-price-small"
							style={{ fontSize: "16px" }}>
							El monto que pedís supera el % máximo de financiación. Para el valor de
							la propiedad,el máximo a financiar es {type === "UI" ? "$" : "US$"}{" "}
							{formatMoney(propertyPrice * 0.8)}
						</Typography.Text>
					) : error ? (
						<Typography.Text className="financing-price-small">
							{"Error al cargar los datos"}
						</Typography.Text>
					) : null}
				</Col>
			</Row>
		</div>
	);
};

